import { useState, useEffect, createRef } from "react";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { FormInput, FormDropDownList, FormNumericTextBox, FormSwitch, FormDatePicker } from "../FormComponents";
import { requiredValidator } from "../Validators";
import { UpdateVehicle, Vehicle, fileDocumentTypes } from "../../../data/interfaces";
import { getEmployees } from "../../../data/employeeData";
import { getPurchaseTypes, getVehicleTypes, getVehicleById, getVehicleDocuments } from "../../../data/vehicleData";
import { FaTrash, FaFilePdf, FaFileCsv, FaFileAlt, FaFileImage, FaExclamationTriangle } from "react-icons/fa";
import SkeletonForm from "../../Skeletons/SkeletonForm";
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab,
} from '@progress/kendo-react-layout';
import {
  Upload,
  UploadOnStatusChangeEvent,
  UploadFileInfo,
  UploadListItemProps,
  ExternalDropZone,
} from '@progress/kendo-react-upload';
import QuoteDocumentRow from '../../QuoteDocumentRow';
import { getDocuments } from '../../../data/jobData';
import Notes from '../../../pages/Notes/Notes';
import { addNoteDTO } from '../../../data/notes';
import VehicleCosts from './VehicleCosts';

interface EditVehicleFormProps {
  onEditSubmit: (vehicle: UpdateVehicle, vehicleId: number) => void;
  onVehicleId: number;
  onVehicleDelete: (vehicleId: number) => void;
  /** False without edit access: the form only shows the vehicle. */
  canEdit?: boolean;
}

const EditVehicleForm = ({ onEditSubmit, onVehicleId, onVehicleDelete, canEdit = true }: EditVehicleFormProps) => {
  const readOnly = !canEdit;
  const [employees, setEmployees] = useState<any[]>([]);
  const [purchaseTypes] = useState(getPurchaseTypes());
  const [vehicleTypes] = useState(getVehicleTypes());
  const [vehicleData, setVehicleData] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [files, setFiles] = useState<UploadFileInfo[]>([]);
  const [documentData, setDocumentData] = useState<fileDocumentTypes[]>([]);
  
  // Get token and API URL from localStorage
  const token = localStorage.getItem('authToken');
  const storedApiURL = localStorage.getItem('apiURL');
  // Fall back to the build-time API URL (as the rest of the app does) before
  // localhost — otherwise production uploads post to https://localhost:7048.
  const apiURL = storedApiURL?.replace('https://', '').replace('http://', '') || process.env.REACT_APP_API_URL || 'localhost:7048';
  
  console.log('Stored API URL:', storedApiURL);
  console.log('Final API URL:', apiURL);
  console.log('Upload token:', token ? 'Present' : 'Missing');
  console.log('Upload URL would be:', `https://${apiURL}/Files/Vehicle/${onVehicleId}`);
  
  const uploadRef = createRef<Upload>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch employees
        const employeeData = await getEmployees();
        const formattedEmployees = employeeData.map(emp => ({
          value: emp.employeeId,
          text: emp.employeeName
        }));
        setEmployees(formattedEmployees);

        // Fetch vehicle data
        if (onVehicleId && onVehicleId > 0) {
          console.log("Fetching vehicle data for ID:", onVehicleId);
          const vehicle = await getVehicleById(onVehicleId);
          console.log("Vehicle data received:", vehicle);
          setVehicleData(vehicle);
          
          // Fetch vehicle documents
          await fetchDocuments();
        } else {
          console.error("Invalid vehicle ID:", onVehicleId);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [onVehicleId]);

  const handleSubmit = (dataItem: any) => {
    // Convert form data to API format
    // Helper function to set time to 3AM to avoid timezone issues
    const setTimeTo3AM = (date: Date | null) => {
      if (!date) return null;
      const newDate = new Date(date);
      newDate.setHours(3, 0, 0, 0);
      return newDate.toISOString();
    };

    const formattedData: UpdateVehicle = {
      vehicleId: onVehicleId,
      registration: dataItem.registration,
      make: dataItem.make || null,
      model: dataItem.model || null,
      employeeId: dataItem.employeeId?.value || null,
      purchaseTypeId: dataItem.purchaseTypeId?.value || null,
      vehicleTypeId: dataItem.vehicleTypeId?.value || null,
      taxDate: setTimeTo3AM(dataItem.taxDate),
      motDate: setTimeTo3AM(dataItem.motDate),
      motBooked: dataItem.motBooked || false,
      serviceDate: setTimeTo3AM(dataItem.serviceDate),
      hasTracker: dataItem.hasTracker || false,
      serviceBooked: dataItem.serviceBooked || false,
      mileage: dataItem.mileage || null,
      leaseEndDate: setTimeTo3AM(dataItem.leaseEndDate),
      leaseMonthlyPayment: dataItem.leaseMonthlyPayment ?? null,
      dateReturnedSold: setTimeTo3AM(dataItem.dateReturnedSold),
    };
    console.log('Submitting vehicle update:', formattedData);
    onEditSubmit(formattedData, onVehicleId);
  };

  const handleDelete = () => {
    setShowDeleteDialog(false);
    onVehicleDelete(onVehicleId);
  };

  const handleSelectedTab = (e: TabStripSelectEventArguments) => {
    setSelectedTab(e.selected);
  };

  const handleUploadSuccess = (event: UploadOnStatusChangeEvent) => {
    setFiles(event.newState);
    // Refresh documents after successful upload
    fetchDocuments();
  };

  const fetchDocuments = async () => {
    try {
      const documents = await getVehicleDocuments(onVehicleId);
      console.log('Fetched vehicle documents:', documents);
      setDocumentData(documents);
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  const handleDocumentDelete = async (documentId: number) => {
    // Refresh documents after deletion
    await fetchDocuments();
  };

  // Custom list item for uploads
  const CustomListItemUI = (props: UploadListItemProps) => {
    const { onRemove, files } = props;

    return (
      <div>
        {files.map((file) => (
          <div
            key={file.uid}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px',
              border: '1px solid #ccc',
              margin: '4px 0',
              borderRadius: '4px',
            }}
          >
            <span>{file.name}</span>
            <button onClick={() => onRemove(file.uid)}>
              Remove
            </button>
          </div>
        ))}
      </div>
    );
  };

  const addNoteDTO: addNoteDTO = {
    enquiryId: null,
    quoteId: null,
    jobId: null,
    vehicleId: onVehicleId
  };

  if (loading || employees.length === 0) {
    return <SkeletonForm />;
  }

  if (!onVehicleId || onVehicleId === 0) {
    return <div>No vehicle selected for editing</div>;
  }

  if (!vehicleData) {
    return <div>Vehicle not found (ID: {onVehicleId})</div>;
  }

  // Prepare initial form values with full objects for dropdowns
  const selectedEmployee = vehicleData.employeeId ? employees.find(emp => emp.value === vehicleData.employeeId) : null;
  const selectedPurchaseType = vehicleData.purchaseTypeId ? purchaseTypes.find(pt => pt.value === vehicleData.purchaseTypeId) : null;
  const selectedVehicleType = vehicleData.vehicleTypeId ? vehicleTypes.find(vt => vt.value === vehicleData.vehicleTypeId) : null;

  // Helper function to parse dates properly
  const parseDate = (dateString: any) => {
    console.log("parseDate input:", dateString, "type:", typeof dateString);
    
    if (!dateString || dateString === "" || dateString === "null" || dateString === "undefined") {
      console.log("No valid date string provided");
      return null;
    }
    
    // If it's already a Date object, return it
    if (dateString instanceof Date) {
      console.log("Already a Date object:", dateString);
      return dateString;
    }
    
    // Try parsing as ISO string
    let date: Date;
    
    if (typeof dateString === 'string') {
      // If it's just a date like "2023-12-19", add time to avoid timezone issues
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        date = new Date(dateString + 'T12:00:00');
        console.log("Parsed date with added time:", date);
      } else {
        date = new Date(dateString);
        console.log("Parsed date directly:", date);
      }
    } else {
      date = new Date(dateString);
      console.log("Parsed date from unknown type:", date);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.log("Invalid date created from:", dateString);
      return null;
    }
    
    console.log("Successfully parsed date:", date);
    return date;
  };

  const initialValues = {
    registration: vehicleData.registration || "",
    make: vehicleData.make || "",
    model: vehicleData.model || "",
    employeeId: selectedEmployee,
    purchaseTypeId: selectedPurchaseType,
    vehicleTypeId: selectedVehicleType,
    taxDate: parseDate(vehicleData.taxDate),
    motDate: parseDate(vehicleData.motDate),
    motBooked: vehicleData.motBooked || false,
    serviceDate: parseDate(vehicleData.serviceDate),
    hasTracker: vehicleData.hasTracker || false,
    serviceBooked: vehicleData.serviceBooked || false,
    mileage: vehicleData.mileage || null,
    leaseEndDate: parseDate(vehicleData.leaseEndDate),
    leaseMonthlyPayment: vehicleData.leaseMonthlyPayment ?? null,
    dateReturnedSold: parseDate(vehicleData.dateReturnedSold),
  };

  console.log("Form initial values:", initialValues);

  return (
    <div className="form__wrapper">
      <div style={{ display: 'flex', width: '100%' }}>
        <TabStrip
          selected={selectedTab}
          onSelect={handleSelectedTab}
          className='form__tabstrip__container'
          animation={false}
        >
          <TabStripTab title='Vehicle Form'>
            <Form
              key={`vehicle-form-${onVehicleId}`}
              onSubmit={handleSubmit}
              initialValues={initialValues}
              render={(formRenderProps) => (
          <FormElement style={{ padding: '1rem', width: '100%', marginBottom: '5rem' }}>
            <fieldset className="k-form-fieldset" disabled={readOnly}>
              <legend className="k-form-legend">Vehicle Details</legend>
              {readOnly && <p className='k-form-hint'>You have view-only access to vehicles.</p>}

              <Field
                name="registration"
                disabled={readOnly}
                component={FormInput}
                label="Registration *"
                validator={requiredValidator}
              />

              <Field
                name="make"
                disabled={readOnly}
                component={FormInput}
                label="Make"
              />

              <Field
                name="model"
                disabled={readOnly}
                component={FormInput}
                label="Model"
              />

              <Field
                name="employeeId"
                disabled={readOnly}
                component={FormDropDownList}
                label="Assigned Employee"
                data={employees}
                textField="text"
                dataItemKey="value"
              />

              <Field
                name="purchaseTypeId"
                disabled={readOnly}
                component={FormDropDownList}
                label="Purchase Type"
                data={purchaseTypes}
                textField="text"
                dataItemKey="value"
              />

              <Field
                name="vehicleTypeId"
                disabled={readOnly}
                component={FormDropDownList}
                label="Vehicle Type"
                data={vehicleTypes}
                textField="text"
                dataItemKey="value"
              />

              <Field
                name="taxDate"
                disabled={readOnly}
                component={FormDatePicker}
                label="Tax Due Date"
              />

              <Field
                name="motDate"
                disabled={readOnly}
                component={FormDatePicker}
                label="MOT Due Date"
              />

              <Field
                name="motBooked"
                disabled={readOnly}
                component={FormSwitch}
                label="MOT Booked"
              />

              <Field
                name="serviceDate"
                disabled={readOnly}
                component={FormDatePicker}
                label="Service Date"
              />

              <Field
                name="mileage"
                disabled={readOnly}
                component={FormNumericTextBox}
                label="Mileage"
              />

              <Field
                name="leaseEndDate"
                disabled={readOnly}
                component={FormDatePicker}
                label="Lease End Date"
              />

              <Field
                name="leaseMonthlyPayment"
                disabled={readOnly}
                component={FormNumericTextBox}
                label="Lease Monthly Payment"
                format="'£'#,##0.00"
                min={0}
              />

              {/* Only offered when editing - a vehicle being added is still on the fleet.
                  Setting this hides the vehicle from the register unless the grid toggle is on. */}
              <Field
                name="dateReturnedSold"
                disabled={readOnly}
                component={FormDatePicker}
                label="Date Returned/Sold"
              />

              <Field
                name="hasTracker"
                disabled={readOnly}
                component={FormSwitch}
                label="Has Tracker"
              />

              <Field
                name="serviceBooked"
                disabled={readOnly}
                component={FormSwitch}
                label="Service Booked"
              />
            </fieldset>

            <div className="form__info" style={{ marginTop: "1rem", fontSize: "0.9rem", color: "#666" }}>
              <p><strong>Created:</strong> {vehicleData.createDate ? new Date(vehicleData.createDate).toLocaleDateString() : 'N/A'} by {vehicleData.employeeCreatedbyName || 'N/A'}</p>
              <p><strong>Modified:</strong> {vehicleData.modifiedDate ? new Date(vehicleData.modifiedDate).toLocaleDateString() : 'N/A'} by {vehicleData.employeeModifiedbyName || 'N/A'}</p>
            </div>

            {!readOnly && (
            <div className='button__container'>
              <button
                type="submit"
                disabled={!formRenderProps.allowSubmit}
                className='form__edit__button edit'
              >
                Update Vehicle
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteDialog(true)}
                className='form__edit__button reject'
              >
                <FaTrash style={{ marginRight: "0.5rem" }} />
                Delete Vehicle
              </button>
            </div>
            )}
          </FormElement>
        )}
      />
    </TabStripTab>
    
    <TabStripTab title={readOnly ? 'Files' : 'File Upload'}>
      {!readOnly && (
      <>
      <ExternalDropZone
        style={{ fontSize: '10pt', fontFamily: 'Heebo, sans-serif' }}
        uploadRef={uploadRef}
      />
      <div style={{ height: '20px' }} />
      <Upload
        ref={uploadRef}
        batch={true}
        files={files}
        multiple={true}
        defaultFiles={[]}
        autoUpload={true}
        withCredentials={false}
        listItemUI={CustomListItemUI}
        saveHeaders={{ Authorization: `Bearer ${token}` }}
        saveUrl={`https://${apiURL}/Files/Vehicle/${onVehicleId}`}
        onStatusChange={handleUploadSuccess}
        onBeforeUpload={(event) => {
          console.log('Before upload event:', event);
          console.log('Upload URL:', `https://${apiURL}/Files/Vehicle/${onVehicleId}`);
          console.log('Token present:', !!token);
        }}
        onAdd={(event) => {
          console.log('Files added:', event.newState);
        }}
      />
      </>
      )}

      <div style={{ height: '1.5rem' }} />

      <div>
        {documentData.length > 0 ? (
          <div>
            {documentData.map((document: fileDocumentTypes) => (
              <QuoteDocumentRow
                key={document.documentId}
                onDocumentId={document.documentId}
                onFileName={document.fileName}
                onFileExtension={document.fileExtension}
                onDocumentDelete={handleDocumentDelete}
                onJobId={0}
                onQuoteId={0}
                appCanView={document.appCanView}
                showVisibilityToggle={false}
                readOnly={readOnly}
              />
            ))}
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              padding: '1rem',
              backgroundColor: 'rgb(216 229 237)',
              borderRadius: '0.5rem',
              alignItems: 'center',
            }}
          >
            <p style={{ margin: 0 }}>No files uploaded yet</p>
          </div>
        )}
      </div>
    </TabStripTab>
    
    <TabStripTab title="Notes">
      <Notes lockEdit={readOnly} sourceEntity={addNoteDTO} />
    </TabStripTab>

    <TabStripTab title="Costs">
      <VehicleCosts vehicleId={onVehicleId} canEdit={canEdit} />
    </TabStripTab>
  </TabStrip>
</div>

      {showDeleteDialog && (
        <Dialog
          title="Confirm Delete"
          onClose={() => setShowDeleteDialog(false)}
        >
          <p>Are you sure you want to delete this vehicle?</p>
          <p><strong>Registration:</strong> {vehicleData.registration}</p>
          <DialogActionsBar>
            <button 
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </button>
            <button 
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-error"
              onClick={handleDelete}
            >
              Confirm Delete
            </button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default EditVehicleForm;